/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useEffect } from 'react';
import { Image, ThumbsUp, ThumbsDown, Tag, Sparkles, ZoomIn, X } from 'lucide-react';
import axios from 'axios';

interface ContentOpinion {
  content_id: string;
  source_id: string;
  emotional_tone: string | null;
  keywords: string[];
  alignment_score: number;
  would_resonate: boolean;
  insight?: string;
}

interface ContentReferenceProps {
  personaId: string;
  personaName: string;
  brand: string;
}

const ContentReference: React.FC<ContentReferenceProps> = ({
  personaId,
  personaName,
  brand,
}) => {
  const [contentOpinions, setContentOpinions] = useState<ContentOpinion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedContent, setSelectedContent] = useState<ContentOpinion | null>(null);

  useEffect(() => {
    if (brand) {
      fetchContentOpinions();
    }
  }, [personaId, brand]);

  const fetchContentOpinions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `/api/personas/${personaId}/content-opinions`,
        { params: { brand, limit: 18 } }
      );
      setContentOpinions(response.data);
    } catch (err) {
      console.error('Error fetching content opinions:', err);
      setError('Failed to load content opinions');
    } finally {
      setIsLoading(false);
    }
  };

  const getAlignmentColor = (score: number) => {
    if (score >= 0.8) return 'from-green-500 to-emerald-500';
    if (score >= 0.6) return 'from-blue-500 to-cyan-500';
    if (score >= 0.4) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-pink-500';
  };

  const getAlignmentText = (score: number) => {
    if (score >= 0.8) return 'Strongly Resonates';
    if (score >= 0.6) return 'Good Match';
    if (score >= 0.4) return 'Somewhat Appeals';
    return 'Low Appeal';
  };

  const getImageUrl = (sourceId: string) => {
    return `/screenshots/${sourceId}.jpg`;
  };

  // Get emotion-based colors for the badges (using hex colors for inline styles)
  const getEmotionStyle = (emotion: string): { bg: string; text: string } => {
    const lowerEmotion = emotion.toLowerCase();
    const emotionColors: Record<string, { bg: string; text: string }> = {
      comfort: { bg: '#F59E0B', text: '#FFFFFF' },      // Amber
      warmth: { bg: '#F97316', text: '#FFFFFF' },       // Orange
      joy: { bg: '#EAB308', text: '#1F2937' },          // Yellow
      anticipation: { bg: '#3B82F6', text: '#FFFFFF' }, // Blue
      authenticity: { bg: '#10B981', text: '#FFFFFF' }, // Emerald
      trust: { bg: '#14B8A6', text: '#FFFFFF' },        // Teal
      nostalgia: { bg: '#A855F7', text: '#FFFFFF' },    // Purple
      excitement: { bg: '#F43F5E', text: '#FFFFFF' },   // Rose
      satisfaction: { bg: '#22C55E', text: '#FFFFFF' }, // Green
      curiosity: { bg: '#06B6D4', text: '#FFFFFF' },    // Cyan
    };
    return emotionColors[lowerEmotion] || { bg: '#374151', text: '#FFFFFF' }; // Gray default
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-2xl p-6 border border-border">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-background-tertiary rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-background-tertiary rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-2xl p-6 border border-border">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="bg-card rounded-2xl p-6 border border-border">
        <p className="text-text-muted text-center">
          Select a brand to see how {personaName} responds to their promotional content
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl p-6 border border-border">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-foreground mb-2">
          How {personaName} Responds to {brand} Content
        </h3>
        <p className="text-sm text-text-muted">
          Based on demographic preferences and emotional alignment
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {contentOpinions.map((opinion) => (
          <div
            key={opinion.content_id}
            className="bg-background-secondary rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 group border border-border"
          >
            {/* Actual Image Display */}
            <div
              className="h-40 bg-background-tertiary relative overflow-hidden cursor-pointer"
              onClick={() => setSelectedContent(opinion)}
            >
              {opinion.source_id ? (
                <img
                  src={getImageUrl(opinion.source_id)}
                  alt={`${brand} promotional content`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling;
                    if (fallback) {
                      (fallback as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
              ) : null}

              {/* Fallback icon (hidden by default) */}
              <div className="absolute inset-0 flex items-center justify-center" style={{ display: 'none' }}>
                <Image className="w-12 h-12 text-text-muted" />
              </div>

              {/* Hover overlay with zoom icon */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3">
                  <ZoomIn className="w-5 h-5 text-gray-800" />
                </div>
              </div>

              {/* Emotional Tone Badge */}
              {opinion.emotional_tone && (
                <div
                  className="absolute top-2 right-2 rounded-full px-2.5 py-1 shadow-lg"
                  style={{ backgroundColor: getEmotionStyle(opinion.emotional_tone).bg }}
                >
                  <span
                    className="text-xs font-semibold capitalize"
                    style={{ color: getEmotionStyle(opinion.emotional_tone).text }}
                  >
                    {opinion.emotional_tone}
                  </span>
                </div>
              )}

              {/* Source ID Badge */}
              {opinion.source_id && (
                <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm rounded px-2 py-1">
                  <span className="text-xs text-white font-mono">
                    {opinion.source_id}
                  </span>
                </div>
              )}
            </div>

            {/* Content Analysis */}
            <div className="p-4 space-y-3">
              {/* Alignment Score */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">Alignment</span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 h-2 bg-background-tertiary rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${getAlignmentColor(opinion.alignment_score)}`}
                      style={{ width: `${opinion.alignment_score * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-text-secondary">
                    {Math.round(opinion.alignment_score * 100)}%
                  </span>
                </div>
              </div>

              {/* Resonance Indicator */}
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold ${
                  opinion.would_resonate ? 'text-green-500' : 'text-orange-500'
                }`}>
                  {getAlignmentText(opinion.alignment_score)}
                </span>
                {opinion.would_resonate ? (
                  <ThumbsUp className="w-4 h-4 text-green-500" />
                ) : (
                  <ThumbsDown className="w-4 h-4 text-orange-500" />
                )}
              </div>

              {/* Keywords */}
              {opinion.keywords.length > 0 && (
                <div className="pt-2 border-t border-border">
                  <div className="flex items-center space-x-1 mb-2">
                    <Tag className="w-3 h-3 text-text-muted" />
                    <span className="text-xs text-text-muted">Key Themes</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {opinion.keywords.slice(0, 3).map((keyword, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 bg-background-tertiary text-xs text-text-secondary rounded-full"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Persona Insight */}
              {opinion.would_resonate && (
                <div className="pt-2 border-t border-border">
                  <div className="flex items-start space-x-2">
                    <Sparkles className="w-3 h-3 text-primary mt-0.5" />
                    <p className="text-xs text-text-secondary italic">
                      "This appeals to my preference for {
                        opinion.keywords[0] || 'quality content'
                      }"
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {contentOpinions.length === 0 && !isLoading && (
        <div className="text-center py-8">
          <Image className="w-12 h-12 text-text-muted mx-auto mb-3" />
          <p className="text-text-muted">
            No content available for {brand}
          </p>
        </div>
      )}

      {/* Enlarged Image Modal */}
      {selectedContent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setSelectedContent(null)}
        >
          <div
            className="bg-card rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-border"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-3">
                {selectedContent.emotional_tone && (
                  <div
                    className="rounded-full px-3 py-1.5 shadow-md"
                    style={{ backgroundColor: getEmotionStyle(selectedContent.emotional_tone).bg }}
                  >
                    <span
                      className="text-sm font-semibold capitalize"
                      style={{ color: getEmotionStyle(selectedContent.emotional_tone).text }}
                    >
                      {selectedContent.emotional_tone}
                    </span>
                  </div>
                )}
                <span className="text-sm text-text-muted font-mono">
                  {selectedContent.source_id}
                </span>
              </div>
              <button
                onClick={() => setSelectedContent(null)}
                className="p-2 rounded-lg hover:bg-background-tertiary transition-colors text-text-muted hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex flex-col md:flex-row">
              {/* Image */}
              <div className="md:w-2/3 bg-background-tertiary">
                <img
                  src={getImageUrl(selectedContent.source_id)}
                  alt={`${brand} promotional content`}
                  className="w-full h-auto max-h-[60vh] object-contain"
                />
              </div>

              {/* Details Panel */}
              <div className="md:w-1/3 p-6 space-y-6 overflow-y-auto max-h-[60vh]">
                {/* Persona Response */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">
                    {personaName}'s Response
                  </h4>
                  <div className={`text-lg font-bold ${
                    selectedContent.would_resonate ? 'text-green-500' : 'text-orange-500'
                  }`}>
                    {getAlignmentText(selectedContent.alignment_score)}
                  </div>
                </div>

                {/* Alignment Score */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">
                    Alignment Score
                  </h4>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-3 bg-background-tertiary rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r ${getAlignmentColor(selectedContent.alignment_score)}`}
                        style={{ width: `${selectedContent.alignment_score * 100}%` }}
                      />
                    </div>
                    <span className="text-lg font-bold text-foreground">
                      {Math.round(selectedContent.alignment_score * 100)}%
                    </span>
                  </div>
                </div>

                {/* Resonance */}
                <div className="flex items-center gap-3">
                  {selectedContent.would_resonate ? (
                    <>
                      <ThumbsUp className="w-6 h-6 text-green-500" />
                      <span className="text-green-500 font-medium">Would resonate with {personaName}</span>
                    </>
                  ) : (
                    <>
                      <ThumbsDown className="w-6 h-6 text-orange-500" />
                      <span className="text-orange-500 font-medium">Lower appeal for {personaName}</span>
                    </>
                  )}
                </div>

                {/* Keywords */}
                {selectedContent.keywords.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Key Themes
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedContent.keywords.map((keyword, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-background-tertiary text-sm text-text-secondary rounded-full border border-border"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI-Generated Persona Insight */}
                <div className={`p-4 rounded-xl border ${
                  selectedContent.would_resonate
                    ? 'bg-green-500/10 border-green-500/20'
                    : 'bg-orange-500/10 border-orange-500/20'
                }`}>
                  <div className="flex items-start gap-3">
                    <Sparkles className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      selectedContent.would_resonate ? 'text-green-500' : 'text-orange-500'
                    }`} />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-2">
                        {selectedContent.would_resonate
                          ? `Why this works for ${personaName}`
                          : `Why this has lower appeal for ${personaName}`
                        }
                      </p>
                      <p className="text-sm text-text-secondary italic">
                        "{selectedContent.insight || 'Loading insight...'}"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentReference;
